/**
 * Query keys de accounting — contabilidad: libros, PyG, punteo, cartera y portal del asesor.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const accountingKeys = {
  reportAccountComposition: {
    detail: (companyId: string | undefined, kind: string, filters: Record<string, unknown>, root: string | undefined, code: string | undefined, page: number) =>
      ['accounting', 'report-account-composition', companyId, kind, filters, root, code, page] as const,
  },
  // ── agentic-erp#925 — actividades económicas + libros IRPF ────────────────
  economicActivity: {
    all: () => ['economic-activities'] as const,
    list: (companyId?: string) => ['economic-activities', companyId] as const,
  },

  irpfBook: {
    all: () => ['irpf-book'] as const,
    list: (
      companyId?: string,
      type?: string,
      activity?: string,
      from?: string,
      to?: string,
    ) => ['irpf-book', type, companyId, activity, from, to] as const,
  },

  chartOfAccounts: {
    all: () => ['chart-of-accounts'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['chart-of-accounts', companyId, filters] as const,
    // Sufijo estructural sugerido por el BE para SubdivisionDialog.
    nextSubaccount: (companyId?: string, parent?: string) =>
      ['next-subaccount', companyId, parent] as const,
    // Mayor auxiliar de terceros: una fila por subcuenta de contacto. Cuelga del
    // prefijo del plan a proposito — reasignar la subcuenta de un contacto crea o
    // renombra filas de `chart_of_accounts`, asi que `all()` invalida las dos cosas.
    auxiliaryAccounts: (companyId?: string) =>
      ['chart-of-accounts', 'auxiliary-accounts', companyId] as const,
  },

  // Las lecturas del Libro Diario / Mayor / Sumas y Saldos viven bajo el prefijo
  // ['accounting', <sub>, companyId, filters] (useAccountingReports). Estas hojas
  // devolvian prefijos SUELTOS ('journal', 'journal-entries'), que React Query no
  // matchea contra ese arbol: las invalidaciones eran no-ops silenciosos y el
  // Diario seguia mostrando datos viejos tras contabilizar. El docblock de `all`
  // afirmaba justo lo contrario de lo que hacia.
  journal: {
    /** Prefijo del Libro Diario — invalida Diario / Mayor / Sumas y Saldos. */
    all: () => ['accounting', 'journal'] as const,
    /** Alias de `all()`: las listas de asientos leen del mismo arbol. */
    entries: () => ['accounting', 'journal'] as const,
    /**
     * Prefijo del detalle de asiento (side-sheet, drill-down y su audit-trail).
     * Sin `id` a proposito: la clave real es
     * ['accounting','journal-entry', companyId, entryId], asi que una hoja con el
     * id en tercera posicion no matchearia nada. Como prefijo invalida los dos
     * lectores (detalle y audit-trail) de golpe, que es lo que piden sus dos
     * call-sites (edicion y extorno de un asiento).
     */
    entry: () => ['accounting', 'journal-entry'] as const,
    /** Cubo general: cualquier query de contabilidad, sin acotar por empresa. */
    accounting: () => ['accounting'] as const,
  },

  // agentic-erp#2758 — el asiento de UN documento (gasto, factura, conciliación) leído
  // por `GET /api/accounting-entries/:id`. Vivía como literal en tres lectores y las
  // invalidaciones apuntaban a `'journalEntry'`, que no casaba con ninguno: tras
  // contabilizar, el drawer seguía pintando el asiento de antes.
  accountingEntry: {
    all: () => ['accounting-entry'] as const,
    detail: (entryId?: string | null, companyId?: string) =>
      ['accounting-entry', entryId, companyId] as const,
  },

  profitLoss: {
    /** Bare prefix — invalidates every PyG query in one shot. */
    all: () => ['profit-loss'] as const,
    /** Report query keyed by (companyId, filters). filters: { startDate, endDate, model, comparison, projectId, costCenterId, depth, includeZero }. */
    report: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['profit-loss', companyId, filters] as const,
  },

  // Imports de diario (lotes) del Centro de Datos, para poder deshacerlos.
  journalImportBatches: {
    all: () => ['journal-import-batches'] as const,
    list: (companyId?: string) => ['journal-import-batches', companyId] as const,
  },

  journalEntryTemplates: {
    /** Bare prefix — invalidate every JE template query. */
    all: () => ['journal-entry-templates'] as const,
    /** List for current company. */
    list: (companyId?: string) => ['journal-entry-templates', companyId] as const,
    /** Single template detail. */
    detail: (id?: string) => ['journal-entry-templates', 'detail', id] as const,
  },

  // ── Fase E G601 — Bulk batch asesor ─────────────────────────────────────────
  // Excepción a LST-08: sin companyId a propósito. Un batch es del despacho y
  // abarca varias empresas; `batchId` (UUID) ya lo identifica.
  bulkBatch: {
    all: () => ['asesor', 'bulk-batch'] as const,
    list: (firmId?: string) => ['asesor', 'bulk-batch', 'list', firmId] as const,
    detail: (batchId?: string) => ['asesor', 'bulk-batch', batchId] as const,
    status: (batchId?: string) => ['asesor', 'bulk-batch', batchId, 'status'] as const,
    companies: (batchId?: string) => ['asesor', 'bulk-batch', batchId, 'companies'] as const,
  },

  // ── Cola "Por hacer" contable (PR9 reinvención workflow) ──────────────────
  // GET /api/accounting/workqueue + POST /decision. `list` keyed por filtros
  // (kind + scope) para que cada combinación cachee su página; `preview` es el
  // dry-run de autoPostBatch para UN item (postable_doc) en el panel detalle.
  workqueue: {
    all: () => ['accounting-workqueue'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['accounting-workqueue', companyId, filters] as const,
    preview: (companyId?: string, itemId?: string) =>
      ['accounting-workqueue', 'preview', companyId, itemId] as const,
  },

  // ── Punteo contable (lettering groups) — open-item matching por cuenta ─────
  // Per-company + `code` (prefijo de cuenta 430/400/subcuenta). `openLines` =
  // pendientes + grupos parciales (PR-1); `lines` = TODAS incl. cerradas (PR-2b).
  punteo: {
    all: () => ['punteo'] as const,
    openLines: (companyId?: string, code?: string) => ['punteo', companyId, code] as const,
    lines: (companyId?: string, code?: string) => ['punteo', 'lines', companyId, code] as const,
    linePairs: (companyId?: string, code?: string, strategy?: string) => ['punteo', 'line-pairs', companyId, code, strategy] as const,
    accounts: (companyId?: string) => ['punteo', 'reconcilable-accounts', companyId] as const,
  },

  // ── Cartera — unidades de ingreso recurrente (motor revenue_units, Fase D) ──
  // `portfolio` = agregado P&L + KPIs por unidad (GET /api/dashboard/portfolio).
  revenueUnit: {
    all: () => ['revenue-unit'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['revenue-unit', companyId, filters] as const,
    portfolio: (companyId?: string) => ['revenue-unit', 'portfolio', companyId] as const,
    // P&L CONTABLE por inmueble (libro mayor, solo posted) — REAV Art. 48.2.3.
    accountingPnL: (companyId?: string, from?: string, to?: string) =>
      ['revenue-unit', 'accounting-pnl', companyId, from, to] as const,
    // Ficha de un activo (drill-down). Comparte prefijo `revenue-unit` →
    // invalidateEntity('revenue-unit') lo refresca al editar la unidad.
    detail: (id?: string, companyId?: string) => ['revenue-unit', 'detail', companyId, id] as const,
    // Asociaciones (E2). Prefijo PROPIO `revenue-unit-link` para invalidar solo
    // los links sin tirar el portfolio entero.
    links: (unitId?: string, companyId?: string) => ['revenue-unit-link', companyId, unitId] as const,
    // Studio — documentos SIN imputar (bandeja). Prefijo del propio documento
    // (`invoice`/`expense`) para que imputar invalide la bandeja correcta.
    unassignedDocs: (docType: string, companyId?: string) => [docType, 'unassigned', companyId] as const,
    // Studio — documentos imputados a una unidad (drawer).
    docs: (docType: string, unitId?: string, companyId?: string) => [docType, 'by-unit', companyId, unitId] as const,
    // Mejora 1 — unidad sugerida para una entidad (cliente/proveedor) al facturar/
    // registrar gasto. Prefijo `revenue-unit` → invalidateEntity('revenue-unit')
    // la refresca al asociar/desasociar.
    suggest: (entityType?: string, entityId?: string, companyId?: string) =>
      ['revenue-unit', 'suggest', companyId, entityType, entityId] as const,
    // Cuadro de amortización del activo enlazado a la unidad (GET
    // /api/accounting/fixed-assets/:assetId/schedule). Cartera REAV.
    amortization: (assetId?: string, companyId?: string) =>
      ['revenue-unit', 'amortization', companyId, assetId] as const,
  },

  // ── Modo Asesor v2 — vistas cross-cartera del Portal del Asesor ────────────
  // Scope: firm (no per-company). El segundo segmento es el firmId para que la
  // invalidación por prefijo pueda limpiar una asesoría concreta.
  asesor: {
    notificationsAll: () => ['asesor', 'notifications'] as const,
    notifications: (firmId?: string, filter = 'open') =>
      ['asesor', 'notifications', firmId, filter] as const,
    feesAll: () => ['asesor', 'fees'] as const,
    feesSummary: (firmId?: string) => ['asesor', 'fees', 'summary', firmId] as const,
    feesInvoices: (firmId?: string, filter = 'pending') =>
      ['asesor', 'fees', 'invoices', firmId, filter] as const,
    reports: (firmId?: string, period?: string) => ['asesor', 'reports', firmId, period] as const,
    cartera: (firmId?: string) => ['asesor', 'cartera', firmId] as const,
    // Mismo discriminador que `cartera`: firm, o `owner:<userId>` en modo grupo (L-04).
    calendar: (firmId?: string, monthISO?: string) => ['asesor', 'calendar', firmId, monthISO] as const,
    team: (firmId?: string) => ['asesor', 'team', firmId] as const,
    navBadges: (firmId?: string) => ['asesor', 'nav-badges', firmId] as const,
  },

  // Catálogo de divisas con el tipo vigente y el cambio fijo de la empresa
  // (GET /api/accounting/currencies).
  currencies: {
    all: () => ['currencies'] as const,
    list: (companyId?: string) => ['currencies', companyId] as const,
  },

  // ── N-FE5 Nivel 3 — semáforo de cierre continuo ───────────────────────────
  // GET /api/accounting/continuous-close (read-only, cache backend 120s).
  continuousClose: {
    all: () => ['continuous-close'] as const,
    status: (companyId?: string) => ['continuous-close', companyId] as const,
  },
} as const;
